import { Component, Injector, Input, OnChanges, OnDestroy } from '@angular/core';

import { BehaviorSubject, Subscription, filter } from 'rxjs';

import { AbstractBaseStyleFieldComponent } from '@Common/PageBuilder/components/BaseStyleField/abstract-base-style-field.component';
import { AbstractPageBuilderService } from '@Common/PageBuilder/services/abstract-page-builder.service';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';

import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';

import { CuratedAdditionalSettingsField } from '../../../Shared/models/curated-additional-settings-field.model';

import { BriefingModel } from '../../models/briefing.model';
import { BriefingsService } from '../../services/briefings.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '@Core/Users/enums/user-permissions.enum';

import { CuratedAutoInsightDialogComponent } from '../CuratedAutoInsightDialog/curated-auto-insight-dialog.component';
import { PrecisionClipSetupTakeoverComponent } from '../PrecisionClipSetupTakeover/precision-clip-setup-takeover.component';
import { Mention } from '@Common/Mentions/models/mention';
import { MentionSearchModel } from '@Monitor/Shared/models/mention-search.model';


@Component({
    selector: 'agpr-curated-additional-settings-field',
    templateUrl: './curated-additional-settings-field.component.html',
    styleUrls: ['./curated-additional-settings-field.component.scss'],
    standalone: false
})
export class CuratedAdditionalSettingsFieldComponent extends AbstractBaseStyleFieldComponent implements OnChanges, OnDestroy {

    /** Model for managing customizations to mentions */
    @Input() declare field: CuratedAdditionalSettingsField;
    @Input() id: string = null;

    briefing: BriefingModel;
    briefingDirty: boolean;

    showInsights: boolean = false;

    hasPrecisionClipPermission: boolean = false;

    private overlayContainer: Element;
    private subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;

    constructor(
        protected coreDialogService: CoreDialogService,
        protected briefingsService: AbstractPageBuilderService,
        public injector: Injector,
        private currentUserStateService: CurrentUserStateService,
    ) {
        super(coreDialogService);

        this.overlayContainer = document.getElementById('main-content');

        this.subscriptions.push(
            this.briefingsService.getFocusedResource().pipe(filter(x => !!x)).subscribe(
                briefingModel => {
                    this.briefing = briefingModel;
                    this.briefingDirty = !this.briefing.equals((this.briefingsService as BriefingsService).getCachedCurrentBriefing());
                }
            ),
            this.currentUserStateService.getPermissions().pipe(filter(x => !!x)).subscribe(
                permissions => {
                    this.hasPrecisionClipPermission = permissions?.includes(userPermissionsEnum.precisionClipper);
                }
            )
        );
    }

    ngOnChanges(changes: any) {
        if (changes?.styles?.currentValue) {
            this.showInsights = changes?.styles?.currentValue.showInsights;
        } else if (changes?.block?.currentValue?.displayElement?.styles) {
            this.showInsights = changes?.block?.currentValue?.displayElement?.styles.showInsights;
        }
    }

    openAutoGenerateDialog() {
        if (this.briefingDirty) {

            return;
        }

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger,
                overlayContainer: this.overlayContainer,
                briefingId: this.briefing.id,
            },
            outputs: {
                dialogClose: () => {}
            }
        };
        this.coreDialogService.open(CuratedAutoInsightDialogComponent, io, this.injector);

    }


    showInsightChange() {
        this.block.displayElement.styles.showInsights = this.showInsights;
        this.changes.emit(this.value);
        (this.briefingsService as BriefingsService).isBoundToGlobalStylesChange(this.block, '', true);
    }

    openPrecisionClipDialog() {
        if (this.briefingDirty) {

            return;
        }

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                resourceId: this.briefing.id,
                animationTrigger,
                overlayContainer: this.overlayContainer,
            },
            outputs: {
                dialogClose: () => {}
            }
        };
        this.coreDialogService.open(PrecisionClipSetupTakeoverComponent, io);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
